import { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import { useStoreSelector } from "~/lib/store/hooks";
import {
  tenantOnboardingStore,
  tenantOnboardingActions,
} from "../store/tenantOnboardingStore";
import { usePostTenants } from "~/api/generated/api";
import { formatTenantRequest } from "../utils/formatters";
import { validateStep1, isStep1Valid } from "../validation/step1Validation";
import { validateStep2, isStep2Valid } from "../validation/step2Validation";
import type { ApiError } from "~/lib/react-query/types";
import type { ZodError } from "zod";
import { triggerGlobalToast } from "~/utils/globalToast";

/**
 * Main hook for tenant onboarding logic
 */
export function useTenantOnboarding() {
  const navigate = useNavigate();
  const toastRef = useRef<Toast>(null);
  const organizationId = useStoreSelector(
    tenantOnboardingStore,
    (state) => state.organizationId,
  );

  const activeStep = useStoreSelector(
    tenantOnboardingStore,
    (state) => state.activeStep,
  );
  const step1Data = useStoreSelector(
    tenantOnboardingStore,
    (state) => state.step1Data,
  );
  const step2Data = useStoreSelector(
    tenantOnboardingStore,
    (state) => state.step2Data,
  );
  const accountManagerData = useStoreSelector(
    tenantOnboardingStore,
    (state) => state.accountManagerData,
  );
  const isDirty = useStoreSelector(
    tenantOnboardingStore,
    (state) => state.isDirty,
  );
  const validationErrors = useStoreSelector(
    tenantOnboardingStore,
    (state) => state.validationErrors,
  );

  const createTenantMutation = usePostTenants({
    mutation: {
      onSuccess: (response) => {
        // Generated hook returns AxiosResponse, data is in response.data
        if (response.data.success && response.data.data) {
          const tenantId = response.data.data.tenantId;
          const tenantName = step1Data.tenant_name;

          // Set success state instead of showing toast
          tenantOnboardingActions.setSuccess(tenantId, tenantName);
          // Clear touched fields
          tenantOnboardingActions.clearTouchedFields();
        }
      },
      onError: (error: Error | ApiError | ZodError) => {
        console.error("Failed to create tenant:", error);

        // Show global error toast with API-provided message/debugMessage when available
        let summary = "Validation error";
        let detail =
          "Something went wrong while creating the tenant. Please check the fields and try again.";

        // Use the error message as summary if available
        const errorMessage =
          (error as ApiError).message ||
          (error instanceof Error ? error.message : null);
        if (errorMessage) {
          summary = errorMessage;
        }

        // Prioritize detailed error messages from API response
        const apiError = error as ApiError;
        const debugMessage = apiError?.debugMessage;
        const errorArray = apiError?.errors;

        // If we have errors array with messages, use the first one as detail
        if (
          Array.isArray(errorArray) &&
          errorArray.length > 0 &&
          errorArray[0]?.message
        ) {
          detail = errorArray[0].message;
        } else if (debugMessage) {
          // Fall back to debugMessage if available
          detail = debugMessage;
        }

        triggerGlobalToast({
          severity: "error",
          summary,
          detail,
          life: 5000,
        });

        // Extract field-specific errors from API error or ZodError
        let apiErrors: Array<{ path?: string[]; message?: string }> = [];

        // Check if it's a ZodError (from schema validation)
        if ((error as ZodError).issues) {
          const zodError = error as ZodError;
          apiErrors = zodError.issues.map((issue) => ({
            path: issue.path,
            message: issue.message,
          }));
        }
        // Check if it's an ApiError (from API response)
        else if (
          (
            error as ApiError & {
              errors?: Array<{ path?: string[]; message?: string }>;
            }
          ).errors
        ) {
          apiErrors =
            (
              error as ApiError & {
                errors?: Array<{ path?: string[]; message?: string }>;
              }
            ).errors || [];
        }

        if (Array.isArray(apiErrors) && apiErrors.length > 0) {
          // Map API errors to form field errors
          const fieldErrors: Record<string, string[]> = {};

          apiErrors.forEach((err: { path?: string[]; message?: string }) => {
            if (err.path && err.path.length > 0 && err.message) {
              // API paths are like ["body", "tenant_name"] or ["domains"]
              // Zod paths are like ["tenant_name"] or ["customer_admin", "given_name"]
              // We need to map them to our form field names
              const fieldPath = err.path.filter((p) => p !== "body");
              const fieldName = fieldPath[fieldPath.length - 1];

              // Handle nested paths like customer_admin.given_name
              if (fieldPath.length > 1 && fieldPath[0] === "customer_admin") {
                // This is a step 2 field
                const step2Field = fieldPath[1];
                if (!fieldErrors[step2Field]) {
                  fieldErrors[step2Field] = [];
                }
                fieldErrors[step2Field].push(err.message);
              } else {
                // This is a step 1 field
                if (!fieldErrors[fieldName]) {
                  fieldErrors[fieldName] = [];
                }
                fieldErrors[fieldName].push(err.message);
              }
            }
          });

          // Set validation errors in store
          if (Object.keys(fieldErrors).length > 0) {
            tenantOnboardingActions.setValidationErrors(fieldErrors);

            // Navigate to the appropriate step if there are errors
            const step1Fields = [
              "tenant_name",
              "type",
              "account_name",
              "domains",
              "total_number_of_users",
              "salesforce_id",
              "country",
              "status",
              "billing_start_date",
              "billing_end_date",
            ];
            const hasStep1Errors = Object.keys(fieldErrors).some((field) =>
              step1Fields.includes(field),
            );

            if (hasStep1Errors) {
              tenantOnboardingActions.setActiveStep(0);
            } else {
              tenantOnboardingActions.setActiveStep(1);
            }
          }
        }
      },
    },
  });

  const handleNext = useCallback(() => {
    if (activeStep === 0) {
      // Validate Step 1 before proceeding
      const errors = validateStep1(step1Data);
      if (Object.keys(errors).length > 0) {
        tenantOnboardingActions.setValidationErrors(errors);
        return;
      }
      tenantOnboardingActions.setActiveStep(1);
    }
  }, [activeStep, step1Data]);

  const handleBack = useCallback(() => {
    if (activeStep === 1) {
      // Clear account manager data when going back
      tenantOnboardingActions.updateAccountManagerData({
        account_manager_id: "",
        account_manager_email: "",
        is_new_account_manager: false,
      });
      tenantOnboardingActions.setActiveStep(0);
    }
  }, [activeStep]);

  const handleSubmit = useCallback(() => {
    // Check online status first
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      triggerGlobalToast({
        severity: "error",
        summary: "No Internet Connection",
        detail:
          "You are currently offline. Please check your internet connection and try again.",
        life: 5000,
      });
      return;
    }

    if (!organizationId) {
      // Show global error toast
      triggerGlobalToast({
        severity: "error",
        summary: "Cannot Create Tenant",
        detail:
          "Organization ID is missing. Please refresh the page and try again.",
        life: 5000,
      });
      // Also show error in console for debugging
      console.error("Organization ID is required");
      return;
    }

    // Mark all fields as touched for validation
    const allFieldNames = [
      "tenant_name",
      "type",
      "account_name",
      "domains",
      "total_number_of_users",
      "salesforce_id",
      "country",
      "status",
      "billing_start_date",
      "billing_end_date",
      "given_name",
      "surname",
      "other_name",
      "preferred_name",
      "contact_email",
      "phone_code",
      "phone_number",
    ];
    allFieldNames.forEach((field) => {
      tenantOnboardingActions.markFieldTouched(field);
    });

    // Validate both steps
    const step1Errors = validateStep1(step1Data);
    const step2Errors = validateStep2(step2Data);

    const allErrors = { ...step1Errors, ...step2Errors };
    if (Object.keys(allErrors).length > 0) {
      tenantOnboardingActions.setValidationErrors(allErrors);
      return;
    }

    // Format and submit
    console.log("accountManagerData before submit:", accountManagerData);
    const requestData = formatTenantRequest(
      step1Data,
      step2Data,
      organizationId,
      accountManagerData.account_manager_id || undefined,
    );
    console.log("Request data being sent:", requestData);
    // Generated hook expects { data: PostTenantsBody }
    createTenantMutation.mutate({ data: requestData });
  }, [
    step1Data,
    step2Data,
    organizationId,
    accountManagerData,
    createTenantMutation,
  ]);

  // Check Step 1 validity: both validation function AND store errors
  const step1ValidationErrors = validateStep1(step1Data);
  const hasStep1StoreErrors = Object.keys(validationErrors).some((field) => {
    // Step 1 fields that can have errors
    const step1Fields = [
      "tenant_name",
      "type",
      "account_name",
      "domains",
      "total_number_of_users",
      "salesforce_id",
      "country",
      "status",
      "billing_start_date",
      "billing_end_date",
      "supports_payroll_plus",
      "payslip_portal_link",
    ];
    return step1Fields.includes(field) && validationErrors[field]?.length > 0;
  });
  const isStep1ValidNow =
    Object.keys(step1ValidationErrors).length === 0 && !hasStep1StoreErrors;

  const isStep2ValidNow = isStep2Valid(step2Data);

  const isSuccess = useStoreSelector(
    tenantOnboardingStore,
    (state) => state.isSuccess,
  );
  const successTenantId = useStoreSelector(
    tenantOnboardingStore,
    (state) => state.successTenantId,
  );
  const successTenantName = useStoreSelector(
    tenantOnboardingStore,
    (state) => state.successTenantName,
  );

  // Check if form is empty (matches initial state)
  const isFormEmpty = useCallback(() => {
    const hasStep1Data =
      step1Data.tenant_name.trim() !== "" ||
      step1Data.account_name.trim() !== "" ||
      step1Data.domains.length > 0 ||
      (step1Data.total_number_of_users !== null &&
        step1Data.total_number_of_users !== "" &&
        step1Data.total_number_of_users !== 0) ||
      step1Data.salesforce_id.trim() !== "" ||
      step1Data.country.trim() !== "" ||
      step1Data.billing_start_date !== null ||
      step1Data.billing_end_date !== null;

    const hasStep2Data =
      step2Data.given_name.trim() !== "" ||
      step2Data.surname.trim() !== "" ||
      step2Data.other_name.trim() !== "" ||
      step2Data.preferred_name.trim() !== "" ||
      step2Data.contact_email.trim() !== "" ||
      step2Data.country.trim() !== "" ||
      step2Data.phone_code.trim() !== "" ||
      step2Data.phone_number.trim() !== "";

    return !hasStep1Data && !hasStep2Data;
  }, [step1Data, step2Data]);

  // Only show leave confirmation if form is dirty AND not empty
  const shouldShowLeaveConfirmation = isDirty && !isFormEmpty();

  return {
    activeStep,
    step1Data,
    step2Data,
    isDirty: shouldShowLeaveConfirmation,
    handleNext,
    handleBack,
    handleSubmit,
    isStep1Valid: isStep1ValidNow,
    isStep2Valid: isStep2ValidNow,
    loading: createTenantMutation.isPending,
    reset: tenantOnboardingActions.reset,
    toastRef,
    isSuccess,
    successTenantId,
    successTenantName,
    clearSuccess: tenantOnboardingActions.clearSuccess,
  };
}
